import { validateJsonObject } from 'Core/data/Helpers';

export class CatalogProcessor {
  
  /**
   * Process an array of items for any catalog entity
   * @param {Array} items - Items to process
   * @param {Object} itemsExists - Existing items
   * @param {string} entityName - Name of entity (e.g., 'tags', 'units', 'drivers', 'assets')
   * @param {Function} getProcessItem - Function to process individual item
   * @param {string} sortField - Field to sort by (default: 'label')
   * @returns {Array} [success, { entityName: processedItems }]
   */
  static executeArray(items, itemsExists, entityName, getProcessItem, sortField = 'label') {
    // Create a Map for O(1) lookups - normalize IDs to strings for consistent comparison
    const itemsMap = new Map((itemsExists[entityName] || []).map(item => [String(item.id), item]));

    // Validate items array
    if (!items || !Array.isArray(items) || items.length === 0) {
      return [true, { [entityName]: Array.from(itemsMap.values()) }];
    }
    
    // Process deletions, updates and creations in a single iteration
    items.forEach(newItem => {
      const itemId = String(newItem.id || newItem.entityId || newItem.CUnits_id);
      const existingItem = itemsMap.get(itemId);
      
      switch (newItem.event) {
        case "deleted":
          if (itemsMap.has(itemId)) {
            itemsMap.delete(itemId);
          }
          break;
          
        case "updated":
          if (existingItem) {
            // Pass itemsExists as third parameter
            itemsMap.set(itemId, getProcessItem(existingItem, newItem, itemsExists));
          }
          break;
          
        case "created":
        default:
          if (existingItem) {
            // Pass itemsExists as third parameter
            itemsMap.set(itemId, getProcessItem(existingItem, newItem, itemsExists));
          } else {
            // Pass itemsExists as third parameter
            itemsMap.set(itemId, getProcessItem({}, newItem, itemsExists));
          }
          break;
      }
      
      if (newItem._deleted && itemsMap.has(itemId)) {
        itemsMap.delete(itemId);
      }
    });

    // Convert Map back to array and sort alphabetically
    const processedItems = Array.from(itemsMap.values()).sort((a, b) => {
      const valueA = (a[sortField] || '').toLowerCase();
      const valueB = (b[sortField] || '').toLowerCase();
      return valueA.localeCompare(valueB);
    });

    return [true, { [entityName]: processedItems }];
  }

  /**
   * Process a single item for any catalog entity
   * @param {Object} item - Item to process
   * @param {Object} itemsExists - Existing items
   * @param {string} entityName - Name of entity
   * @param {Function} getProcessItem - Function to process individual item
   * @param {string} sortField - Field to sort by
   * @returns {Array} [success, { entityName: processedItems }]
   */
  static execute(item, itemsExists, entityName, getProcessItem, sortField = 'label') {
    // Create a Map for O(1) lookups - normalize IDs to strings for consistent comparison
    const itemsMap = new Map((itemsExists[entityName] || []).map(entity => [String(entity.id), entity]));
    
    // Validate item
    if (!item) {
      return [true, { [entityName]: Array.from(itemsMap.values()) }];
    }
    
    const itemId = String(item.id || item.entityId || item.CUnits_id);
    const existingItem = itemsMap.get(itemId);
    
    switch (item.event) {
      case "deleted":
        if (itemsMap.has(itemId)) {
          itemsMap.delete(itemId);
        }
        break;
        
      case "updated":
        if (existingItem) {
          // Pass itemsExists as third parameter
          itemsMap.set(itemId, getProcessItem(existingItem, item, itemsExists));
        }
        break;
        
      case "created":
      default:
        if (existingItem) {
          // Pass itemsExists as third parameter
          itemsMap.set(itemId, getProcessItem(existingItem, item, itemsExists));
        } else {
          // Pass itemsExists as third parameter
          itemsMap.set(itemId, getProcessItem({}, item, itemsExists));
        }
        break;
    }
    
    if (item._deleted && itemsMap.has(itemId)) {
      itemsMap.delete(itemId);
    }

    const processedItems = Array.from(itemsMap.values()).sort((a, b) => {
      const valueA = (a[sortField] || '').toLowerCase();
      const valueB = (b[sortField] || '').toLowerCase();
      return valueA.localeCompare(valueB);
    });

    return [true, { [entityName]: processedItems }];
  }


  /**
   * Reconstruct tags array from tagIds string
   * @param {string} tagIds - Comma-separated or JSON array string of tag IDs
   * @param {Object} itemsExists - Current state with tags catalog
   * @returns {Array} Array of tag objects
   */
  static reconstructTagsFromIds(tagIds, itemsExists) {
    if (!tagIds || !itemsExists.tags) {
      return [];
    }

    let tagIdArray = [];

    // Handle different tagIds formats
    if (typeof tagIds === 'string') {
      // Check if it's a JSON array string like "[23935,43882]"
      if (tagIds.startsWith('[') && tagIds.endsWith(']')) {
        tagIdArray = validateJsonObject(tagIds);
      } else {
        // Handle comma-separated string "23935,43882"
        tagIdArray = tagIds.split(',').filter(Boolean).map(id => Number(id.trim()));
      }
    } else if (Array.isArray(tagIds)) {
      tagIdArray = tagIds;
    }

    // Create a Map of tags for O(1) lookup
    const tagsMap = new Map(
      itemsExists.tags.map(tag => [Number(tag.id), tag])
    );

    // Reconstruct tags array maintaining the order from tagIds
    const reconstructedTags = tagIdArray
      .map(tagId => {
        const tag = tagsMap.get(Number(tagId));
        if (!tag) {
          return null;
        }
        return tag;
      })
      .filter(Boolean); // Remove nulls

    return reconstructedTags;
  }
}